import { useBreakpoint } from '@chakra-ui/media-query';
import { useDisclosure } from '@chakra-ui/react';
import { type ReactNode, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { FeatureFlag, LOGIN } from '@revelio/core';

import packageJson from '../../package.json';
import { environment } from '../environments/environment';
import useGoogleAnalytics from '../hooks/analytics/useGoogleAnalytics';
import { useBrandMeta } from '../shared/branding/brand-provider';
import { SelectionListProvider } from '../shared/selection-lists/SelectionListProvider';
import {
  useUnleashFlag,
  useUnleashVersionRefresh,
} from '../shared/unleash-flags/use-unleash-flag';
import '../styles/main.css';

import { PrivateRoute } from './Auth/private-route';
import { PageManagerProvider } from './CustomDash/components/PageManagerProvider/PageManagerProvider';
import { SharedPlotViewer } from './CustomDash/components/SharedReport/SharedPlotViewer';
import { SharedReportViewer } from './CustomDash/components/SharedReport/SharedReportViewer';
import './CustomDash/styles/print.css';

import { ImpersonationBanner } from './adminRewrite/impersonation/banner/ImpersonationBanner';
import { ImpersonationModal } from './adminRewrite/impersonation/settings/ImpersonationModal';
import GameV2 from './gameV2/GameV2';
import { GlobalAIAssistantProvider } from './global-ai-assistant/global-ai-assistant-context';
import { GlobalAIAssistantHost } from './global-ai-assistant/global-ai-assistant-host';
import { IndividualPotentialPage } from './individual-potential/IndividualPotentialPage';
import Landing from './landing/Landing';
import Login from './login/login';
import { MobileUnsupportedPage } from './MobileUnsupportedPage';
import './next.module.css';

import { RequireAuth } from './routes/ProtectedRoute';
import { reducedRoutes } from './routes/reduced-routes';
import { getNavItems } from './routes/routes';
import SideNav from './routes/side-nav/side-nav';
import Tour from './tour/tour';

const version = packageJson.version;

function GlobalAssistantShell({ children }: { children: ReactNode }) {
  const globalAssistantEnabled = useUnleashFlag(FeatureFlag.GlobalAIAssistant);
  if (globalAssistantEnabled) {
    return (
      <GlobalAIAssistantProvider>
        {children}
        <GlobalAIAssistantHost />
      </GlobalAIAssistantProvider>
    );
  }
  return children;
}

export function Next() {
  useGoogleAnalytics();
  useUnleashVersionRefresh();
  useBrandMeta();
  const isImpersonationEnabled = useUnleashFlag(FeatureFlag.Impersonation);
  const isIndividualPotentialEnabled = useUnleashFlag(
    FeatureFlag.IndividualPotential
  );
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const navItems = useMemo(
    () => getNavItems(isNewDashboardLayout),
    [isNewDashboardLayout]
  );
  const routes = useMemo(() => reducedRoutes(navItems), [navItems]);
  const {
    isOpen: isImpersonationModalOpen,
    onOpen: onImpersonationModalOpen,
    onClose: onImpersonationModalClose,
  } = useDisclosure();
  const handleOpenImpersonationModal = isImpersonationEnabled
    ? onImpersonationModalOpen
    : undefined;

  const breakpoint = useBreakpoint({ ssr: false });
  if (
    environment.production &&
    (breakpoint === 'sm' || breakpoint === 'base')
  ) {
    return <MobileUnsupportedPage />;
  }

  return (
    <>
      <ImpersonationBanner onEditImpersonation={handleOpenImpersonationModal} />
      <Routes>
        <Route path={`/${LOGIN}`} element={<Login />} />
        <Route path="/shared/plot/:token" element={<SharedPlotViewer />} />
        <Route path="/shared/:token" element={<SharedReportViewer />} />
        <Route path="/" element={<PrivateRoute />}>
          <Route
            element={
              <SelectionListProvider>
                <Tour />
                <PageManagerProvider>
                  <GlobalAssistantShell>
                    <SideNav
                      items={navItems}
                      version={version}
                      onOpenImpersonationModal={handleOpenImpersonationModal}
                    />
                  </GlobalAssistantShell>
                </PageManagerProvider>
              </SelectionListProvider>
            }
          >
            <Route index element={<Landing />} />

            {routes}
          </Route>
          <Route
            path="/gameV2"
            element={
              <RequireAuth adminRequired>
                <GameV2 />
              </RequireAuth>
            }
          />
          <Route
            path="/individual-potential"
            element={
              isIndividualPotentialEnabled ? (
                <SelectionListProvider>
                  <IndividualPotentialPage />
                </SelectionListProvider>
              ) : (
                <Navigate to="/" />
              )
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      {isImpersonationEnabled && (
        <ImpersonationModal
          isOpen={isImpersonationModalOpen}
          onClose={onImpersonationModalClose}
        />
      )}
    </>
  );
}

export default Next;
